import { useCallback, useEffect, useRef, useState } from 'react'

/** How long a tapped overlay stays before it hides itself (a phone has no hover to leave). */
export const TIP_TAP_MS = 2500

/**
 * A small overlay's open state for a mark that explains itself ("Pinned by the moderators"):
 * hover and keyboard focus are CSS; this is the TAP. A tap on iOS does not focus a button, so
 * the click toggles `open`, which hides itself after `TIP_TAP_MS` or on blur.
 */
export function useTipToggle(): {
  open: boolean
  onClick: () => void
  onBlur: () => void
} {
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clear = useCallback((): void => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])
  useEffect(() => clear, [clear])
  const onClick = useCallback((): void => {
    clear()
    setOpen(value => {
      if (!value) {
        timer.current = setTimeout(() => setOpen(false), TIP_TAP_MS)
      }
      return !value
    })
  }, [clear])
  const onBlur = useCallback((): void => {
    clear()
    setOpen(false)
  }, [clear])
  return { open, onClick, onBlur }
}
